// =============================================================================
// Company switch and invitations (CM-Central Guides/TENANCY-DESIGN.md §3.2, §5.1).
//
// One login can belong to several companies; a session is in exactly one. The
// server decides: SwitchTenant re-checks that the caller belongs to the target
// company and mints a new token for it. The client never sends a tenant id as a
// data filter.
//
// Switching is a NEW SESSION in another company, so everything cached for the
// previous one is dropped (clearAuthToken: persisted Redux state, GET response
// cache) and the app hard-reloads — Redux slices, SignalR connections and any
// in-memory caches then start from the new token. Same approach as
// stopImpersonation.
// =============================================================================

import axiosInstance from "../data/interface/axiosInstance";
import { getBaseUrl } from "../data/interface/getBaseUrl";
import { clearAuthToken } from "../data/interface/authStorage";
import { getLandingRoute } from "../common/tenancy/landingRoute";
import type { TenantOption } from "../data/redux/authSlice";

const baseURL = getBaseUrl(true);

export interface PendingInvite {
  token: string;
  tenantId: number;
  tenantName: string;
  invitedBy?: string;
  expiresAt?: string;
}

export interface MyTenants {
  currentTenantId: number;
  tenants: TenantOption[];
  pendingInvites: PendingInvite[];
}

export interface InviteDetails {
  kind: "employee" | "customer";
  tenantName: string;
  email: string;
  name?: string;
  expiresAt?: string;
  isExpired: boolean;
}

// Company-scoped data kept outside the GET cache that clearAuthToken sweeps.
const COMPANY_SCOPED_KEYS = ["orderFormCache"];
const COMPANY_SCOPED_SESSION_KEYS = ["kbv_sk"];

const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.responseMessage || error?.message || fallback;

export const getMyTenants = async (): Promise<MyTenants | null> => {
  try {
    const { data } = await axiosInstance.get(`${baseURL}/Account/MyTenants`, { params: { clientType: "web" } });
    return !data?.isError && data?.dataModel ? data.dataModel : null;
  } catch {
    return null;
  }
};

/**
 * Switches the session to another company and reloads the app inside it.
 * Resolves only on failure (the page navigates away on success).
 */
export const switchTenant = async (tenantId: number): Promise<void> => {
  let payload: any;
  try {
    const { data } = await axiosInstance.post(`${baseURL}/Account/SwitchTenant`, {
      tenantId,
      clientType: "web",
      deviceToken: localStorage.getItem("deviceToken") || null,
    });
    if (data?.isError || !data?.dataModel?.token) {
      throw new Error(data?.responseMessage || "Could not switch company.");
    }
    payload = data.dataModel;
  } catch (error: any) {
    throw new Error(errorMessage(error, "Could not switch company."));
  }

  // Previous company's session and cached data out first...
  clearAuthToken();
  COMPANY_SCOPED_KEYS.forEach((key) => localStorage.removeItem(key));
  COMPANY_SCOPED_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));

  // ...then the new company's session, in the same shape login.tsx writes, so
  // the reload hydrates Redux (authSlice / permissionsSlice read these keys).
  localStorage.setItem("authToken", payload.token);
  if (payload.userId) localStorage.setItem("userid", String(payload.userId));
  localStorage.setItem(
    "userProfile",
    JSON.stringify({
      userId: payload.userId,
      name: payload.name,
      email: payload.email,
      roles: payload.roles || [],
      vanPhoto: payload.vanPhoto || null,
      startYourDay: payload.startYourDay || false,
    })
  );
  if (payload.mustChangePassword) localStorage.setItem("mustChangePassword", "true");
  localStorage.setItem(
    "AuthState",
    JSON.stringify({
      user: {
        name: payload.name,
        email: payload.email,
        tenantName: payload.tenantName,
        tenantId: payload.tenantId,
        tenants: Array.isArray(payload.tenants) ? payload.tenants : [],
        pendingInviteCount: payload.pendingInviteCount || 0,
        isPlatformAdmin: !!payload.isPlatformAdmin,
      },
      isAuthenticated: true,
      loading: false,
      error: null,
    })
  );
  localStorage.setItem(
    "permissionsState",
    JSON.stringify({
      modules: [],
      permissions: [],
      permissionKeys: Array.isArray(payload.permissionKeys) ? payload.permissionKeys : [],
      // The new company's product modules.
      enabledModules: Array.isArray(payload.enabledModules) ? payload.enabledModules : null,
    })
  );

  window.location.replace(getLandingRoute(payload.permissionKeys, !!payload.isPlatformAdmin));
};

// Anonymous: the invitation token from the emailed link is the authorisation.
export const getInvite = async (token: string): Promise<InviteDetails> => {
  try {
    const { data } = await axiosInstance.get(`${baseURL}/Account/TenantInvite`, { params: { token } });
    if (data?.isError || !data?.dataModel) throw new Error(data?.responseMessage || "Invitation not found.");
    return data.dataModel;
  } catch (error: any) {
    throw new Error(errorMessage(error, "This invitation link is invalid or has already been used."));
  }
};

export const respondToInvite = async (token: string, accept: boolean): Promise<string> => {
  try {
    const { data } = await axiosInstance.post(`${baseURL}/Account/RespondTenantInvite`, { token, accept });
    if (data?.isError) throw new Error(data?.responseMessage || "Could not answer the invitation.");
    return data?.responseMessage || (accept ? "Invitation accepted." : "Invitation declined.");
  } catch (error: any) {
    throw new Error(errorMessage(error, "Could not answer the invitation."));
  }
};
